import axios from "axios";
import config from "../../../config";
import dayjs from "dayjs";
// import junctionData from "./json_files/junction.json";
// import rcmData from "./json_files/rcm.json";

const SYNCHROEU_URL = config.SYNCHROEU_URL;
const API_URL = config.TRAFFIC;
let rcmIdOrder = [];

// helper

function getAuthHeaders() {
  const token = localStorage.getItem("keycloak-token");
  if (!token) {
    throw new Error("Missing auth token — please login again.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function groupJunctions(data) {
  if (!Array.isArray(data)) return [];

  const map = new Map();

  for (const item of data) {
    const key = item?.junction_id;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        type: "junction",
        name_en: item?.name_junction_en ?? "",
        name_el: item?.name_junction ?? "",
        geom: item?.centroid ?? null,
      });
    }
  }

  return Array.from(map.values());
}

function groupRcms(data) {
  if (!Array.isArray(data)) return [];

  const map = new Map(); // preserves insertion order

  for (const item of data) {
    const id =
      item?.id ??
      item?.junction_id ?? // fallback just in case
      item?.intersection_id; // fallback just in case
    if (id == null) continue;

    if (!map.has(id)) {
      map.set(id, {
        id,
        type: "rcm",
        name_en: item?.name_en ?? item?.name ?? "",
        name_el: item?.name_el ?? item?.name ?? "",
        geom: item?.geom ?? item?.geometry ?? null,
      });
    }
  }

  return Array.from(map.values());
}

function sortJunction(data) {
  const cmpNum = (a, b) => (a ?? Infinity) - (b ?? Infinity);
  const cmpStr = (a, b) => String(a ?? "").localeCompare(String(b ?? ""));

  return (data ?? [])
    .map((j) => {
      const approaches = (j.approacheslivedata ?? [])
        .slice()
        .sort((a, b) => {
          const byFlow = cmpNum(a?.flow, b?.flow);
          if (byFlow !== 0) return byFlow;
          const byName = cmpStr(a?.name, b?.name);
          if (byName !== 0) return byName;
          return cmpNum(a?.id, b?.id);
        })
        .map((a) => ({
          ...a,
          turnRatios: (a.turnRatios ?? []).slice().sort((x, y) => {
            const byDir = cmpNum(x?.direction, y?.direction);
            if (byDir !== 0) return byDir;
            return cmpNum(x?.exitId, y?.exitId);
          }),
        }));

      return { ...j, approacheslivedata: approaches };
    })
    .sort((a, b) => cmpNum(a?.junction_id, b?.junction_id));
}

function convertJunction(junctions) {
  const features = [];

  for (const j of junctions || []) {
    const junctionId = j?.junction_id;
    const approaches = Array.isArray(j?.approacheslivedata)
      ? j.approacheslivedata
      : [];

    for (const a of approaches) {
      const {
        id: approachId,
        flow,
        name,
        timestamp,
        viewState,
        volumePerHour,
      } = a;

      const turns = Array.isArray(a?.turnRatios) ? a.turnRatios : [];
      for (const t of turns) {
        // require a point geometry exactly like your target
        if (
          !t?.geometry?.type ||
          t.geometry.type !== "Point" ||
          !Array.isArray(t.geometry.coordinates)
        )
          continue;

        const id = String(t.exitId);
        const direction = t.direction ?? null;
        const ratioPercent = t.ratioPercent ?? 0;

        features.push({
          type: "Feature",
          id,
          geometry: {
            type: "Point",
            coordinates: [
              Number(t.geometry.coordinates[0]),
              Number(t.geometry.coordinates[1]),
            ],
          },
          properties: {
            junction: junctionId,
            approacheslivedata_id: approachId,
            flow,
            direction,
            ratioPercent,
            volumePerHour,
            text_field: `${Math.round(ratioPercent)} %`,
            metric_value_ratioPercent: `${Math.round(ratioPercent)} %`,
            metric_value_volumePerHour: `${Math.round(
              volumePerHour ?? 0
            )} vehicles/hr`,
            timestamp: dayjs(timestamp).format("HH:mm, DD/MM/YYYY"),
            viewState,
            name,
            _id: id,
            displayMessages: `<div>
        <p>${name}</p>
        <p>Flow: ${flow}</p>
        <p>Ratio: ${Math.round(ratioPercent)} %</p>
        <p>Volume: ${Math.round(volumePerHour ?? 0)} vehicles/hr</p>
      </div>`,
          },
        });
      }
    }
  }

  return { type: "FeatureCollection", features };
}

function sortRcm(data, idOrder) {
  if (!Array.isArray(data)) return [];

  const orderIndex = new Map((idOrder ?? []).map((id, i) => [Number(id), i]));

  const getRank = (junctionId) =>
    orderIndex.has(Number(junctionId))
      ? orderIndex.get(Number(junctionId))
      : Number.POSITIVE_INFINITY;

  const getFirstApproach = (item) =>
    Array.isArray(item?.approacheslivedata) && item.approacheslivedata.length
      ? item.approacheslivedata[0]
      : null;

  const seen = new Set();

  return data
    .filter((item) => {
      const approach = getFirstApproach(item);
      if (!approach) return true; // keep if no approach data

      const key = `${item.junction_id}_${approach.flow}_${approach.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice()
    .sort((a, b) => {
      const ra = getRank(a?.junction_id);
      const rb = getRank(b?.junction_id);
      if (ra !== rb) return ra - rb;

      const aa = getFirstApproach(a);
      const bb = getFirstApproach(b);

      const aflow = aa?.flow ?? Number.POSITIVE_INFINITY;
      const bflow = bb?.flow ?? Number.POSITIVE_INFINITY;
      if (aflow !== bflow) return aflow - bflow;

      const adir = aa?.direction ?? Number.POSITIVE_INFINITY;
      const bdir = bb?.direction ?? Number.POSITIVE_INFINITY;
      if (adir !== bdir) return adir - bdir;

      const aid = String(aa?.id ?? "");
      const bid = String(bb?.id ?? "");
      return aid.localeCompare(bid);
    });
}

function convertRcm(rcmData) {
  const features = [];

  for (const item of rcmData ?? []) {
    const junctionId = item?.junction_id ?? 1;
    const coords = item?.geometry?.coordinates ?? [0, 0];

    const approaches = Array.isArray(item?.approacheslivedata)
      ? item.approacheslivedata
      : [];

    for (const a of approaches) {
      const approachId = a?.id;
      const flow = a?.flow ?? null;
      const name = a?.name ?? "";
      const timestamp = a?.timestamp ?? null;
      const viewState = a?.viewState ?? null;
      const volumePerHour = a?.volumePerHour ?? 0;

      // ✅ defaults so layers never fail
      const direction = a?.direction ?? 1; // default to 1 if missing
      const ratioPercent = Number.isFinite(a?.ratioPercent)
        ? a.ratioPercent
        : 0; // always a number

      const id = String(approachId);

      features.push({
        type: "Feature",
        id,
        geometry: {
          type: "Point",
          coordinates: [Number(coords[0] ?? 0), Number(coords[1] ?? 0)],
        },
        properties: {
          junction: junctionId,
          approacheslivedata_id: approachId,
          flow,
          direction,
          ratioPercent,
          volumePerHour,
          text_field: `${Math.round(volumePerHour)} vehicles/hr`,
          metric_value_ratioPercent: `${Math.round(ratioPercent)} %`,
          metric_value_volumePerHour: `${Math.round(
            volumePerHour
          )} vehicles/hr`,
          timestamp: dayjs(timestamp ?? undefined).format("HH:mm, DD/MM/YYYY"),
          viewState,
          name,
          _id: id,
          displayMessages: `<div>
        <p>${name}</p>
        <p>Flow: ${flow ?? "N/A"}</p>
        <p>Ratio: ${Math.round(ratioPercent)} %</p>
        <p>Volume: ${Math.round(volumePerHour)} vehicles/hr</p>
      </div>`,
        },
      });
    }
  }

  return { type: "FeatureCollection", features };
}

/**
 *
 * ---------- API calls ----------
 *
 */

export async function fetchJunctionList() {
  try {
    const res = await axios.get(
      `${SYNCHROEU_URL}/pg_junction_thess/list_junctions`,
      { headers: getAuthHeaders() }
    );
    return groupJunctions(res.data);
  } catch (err) {
    console.error("fetchJunctionList failed:", err);
    return []; // Fallback so caller doesn't break
  }
}

export async function fetchRcmList() {
  try {
    const res = await axios.get(`${API_URL}/intersections`, {
      headers: { "Accept-Profile": "detectors" },
    });

    rcmIdOrder = (res.data ?? []).map((item) => item.id);
    return groupRcms(res.data);
  } catch (err) {
    console.error("Error fetching RCM list:", err.message || err);
    return []; // fallback so UI doesn't crash
  }
}

export async function fetchJunctionData() {
  const headers = {
    ...getAuthHeaders(),
    "Accept-Profile": "public",
  };

  try {
    const res = await axios.get(
      `${SYNCHROEU_URL}/pg_junction_thess/junctions_live_data_view`,
      { headers }
    );

    const sortData = sortJunction(res.data[0].fulldata);
    const data = convertJunction(sortData);

    return data;
  } catch (error) {
    console.error(
      "Error fetching junction data:",
      error.response?.data || error.message
    );
    throw error; // Rethrow for upstream handling if needed
  }
}

export async function fetchRcmData() {
  const res = await axios.get(`${API_URL}/flow_location_live_data_view`, {
    headers: { "Accept-Profile": "detectors" },
  });

  const sortData = sortRcm(res.data[0].result[0].fulldata, rcmIdOrder);
  const data = convertRcm(sortData);

  return data;
}
